// ---------------------------------------------------------------------------
// Printing what a completed sale owes.
//
// THE ORDER OF EVENTS, and it is the whole safety argument:
//
//   1. the sale completes and commits - money, stock, shift, all done;
//   2. the response says what paper is owed and which layout each piece uses;
//   3. THEN this hook renders those documents and posts the pixels.
//
// Nothing here can reach back into the sale. By the time it runs, the sale is
// a committed row; a printer that is unplugged produces a warning and a
// retryable job, never a reversal.
//
// WHY DUPLICATES CANNOT HAPPEN. The idempotency key is built on the SERVER
// from the sale id and the document type. Every call for the same sale
// produces the same key, so a refreshed till, a re-mounted component, a
// retried request and a reopened sale all find the job already there and add
// nothing. `sentFor` below is a local optimisation to avoid the wasted round
// trip - it is not the guarantee, and it does not need to be.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueuePrintJob, type PrintJobStatus } from '../api/printJobs.api';
import { dotsForRoll, rasteriseElement, RasterError } from '../utils/rasterise';

/// One document the sale owes, as the backend described it.
export interface PlannedDocument {
  printerRole: 'CASHIER_RECEIPT' | 'KITCHEN_TICKET';
  documentType: 'CASHIER_RECEIPT' | 'KITCHEN_TICKET';
  designVersionId: string | null;
  kitchenTicketId?: string;

  /// WHICH PHYSICAL PRINTER this piece of paper is for, chosen by the server:
  /// the shop's receipt printer, or the bench frozen onto this ticket.
  ///
  /// The till used to drop this field and send only the ROLE, leaving the
  /// server to resolve "the kitchen printer" - a thing that stopped existing
  /// the day a shop could own a grill bench and a meat bench. Null is a real
  /// answer, meaning nothing has been chosen yet.
  printerProfileId: string | null;
  /// What the owner calls it, so a message can say «مطبخ المشاوي».
  printerName: string | null;
}

/// How one planned document is told apart from the others.
///
/// NOT the role. A sale that touched two benches owes two KITCHEN_TICKET
/// documents, and keying anything by role made the second overwrite the first:
/// one status, one toast, and two jobs that were the same photograph.
export function documentKey(document: PlannedDocument): string {
  return document.kitchenTicketId ?? document.printerRole;
}

/// What a record owes the printers.
///
/// Exactly one of `saleId` and `orderId` is set. A delivery order's
/// preparation ticket and the sale that later pays for it are two different
/// records, and the server keys their jobs separately - which is what stops
/// paying for an order from printing its ticket a second time.
export interface PrintPlan {
  saleId: string | null;
  orderId: string | null;
  documents: PlannedDocument[];
}

/// What the till is showing about one document.
export interface DocumentPrintState {
  /// Unique per document, so two benches do not share one status.
  key: string;
  role: 'CASHIER_RECEIPT' | 'KITCHEN_TICKET';
  /// The bench or counter this document went to, for a message that can name
  /// it. Null when the shop has not chosen one.
  printerName: string | null;
  status: 'PREPARING' | PrintJobStatus;
  jobId: string | null;
  /// Written for a cashier. Never a socket error, never a stack trace.
  error: string | null;
  /// True when this document is queued for a shop print agent that has not
  /// been heard from. The difference between "the printer is busy" and "there
  /// is nobody in the shop to print this", which used to read the same.
  agentSilent?: boolean;
}

/// Where to find each rendered document on the page.
///
/// The caller renders the sheets off-screen and hands over their elements. The
/// SHEET is what gets photographed, not its frame: capturing a wrapper would
/// print the modal background as a black rectangle.
export interface SheetRefs {
  cashier: () => HTMLElement | null;
  /// The sheet for ONE ticket, by its id. A single getter returned the same
  /// element for every bench, so the meat bench was sent a picture of the
  /// grill's list - a worse failure than printing nothing, because somebody
  /// acts on it.
  kitchen: (kitchenTicketId: string) => HTMLElement | null;
}

export function useSalePrinting(sheets: SheetRefs) {
  const queuePrint = useQueuePrintJob();
  const [states, setStates] = useState<DocumentPrintState[]>([]);

  /// Sales this hook has already acted on, so a re-render does not re-post.
  /// The server would refuse the duplicate anyway; this saves the trip.
  const sentFor = useRef(new Set<string>());
  const gone = useRef(false);

  useEffect(() => {
    gone.current = false;
    return () => {
      gone.current = true;
    };
  }, []);

  const update = useCallback((key: string, patch: Partial<DocumentPrintState>) => {
    if (gone.current) return;
    setStates((current) =>
      current.map((state) => (state.key === key ? { ...state, ...patch } : state)),
    );
  }, []);

  /// Print everything a sale owes.
  ///
  /// Safe to call more than once for the same sale: the second call does
  /// nothing locally, and would be refused server-side even if it did not.
  const printForSale = useCallback(
    async (
      plan: PrintPlan,
      widths: {
        cashier: 58 | 80;
        /// Per ticket: two benches may be printed from designs stamped at
        /// different times, and a roll width is a property of the design that
        /// piece of paper was written under.
        kitchen: (kitchenTicketId: string) => 58 | 80;
      },
    ) => {
      const subject = plan.saleId ?? plan.orderId;
      if (!subject || plan.documents.length === 0) return;
      if (sentFor.current.has(subject)) return;
      sentFor.current.add(subject);

      setStates(
        plan.documents.map((document) => ({
          key: documentKey(document),
          role: document.printerRole,
          printerName: document.printerName,
          status: 'PREPARING' as const,
          jobId: null,
          error: null,
        })),
      );

      for (const document of plan.documents) {
        const isCashier = document.printerRole === 'CASHIER_RECEIPT';
        const key = documentKey(document);
        const ticketId = document.kitchenTicketId ?? '';

        try {
          const element = isCashier ? sheets.cashier() : sheets.kitchen(ticketId);
          if (!element) throw new RasterError('That document could not be prepared.');

          const raster = await rasteriseElement(element, {
            pixelWidth: dotsForRoll(isCashier ? widths.cashier : widths.kitchen(ticketId)),
          });
          if (gone.current) return;

          const response = await queuePrint.mutateAsync({
            printerRole: document.printerRole,
            documentType: document.documentType,
            raster,
            saleId: plan.saleId,
            orderId: plan.orderId,
            kitchenTicketId: document.kitchenTicketId ?? null,
            // THE PRINTER THE SERVER NAMED, carried through untouched. This is
            // the whole of the routing decision; the role beside it is only
            // what KIND of document this is.
            printerProfileId: document.printerProfileId,
            ...(isCashier
              ? { receiptDesignVersionId: document.designVersionId }
              : { kitchenDesignVersionId: document.designVersionId }),
            immediate: true,
          });

          update(key, {
            jobId: response.job.id,
            status: response.result?.status ?? response.job.status,
            error: response.result?.error ?? null,
            // The server only sends this when the paper is now the shop
            // agent's to produce. `seenRecently: false` means nothing has
            // asked for work, so this document is not "queued for retry" -
            // it is queued for nobody.
            agentSilent: response.awaitingAgent ? !response.awaitingAgent.seenRecently : false,
          });
        } catch (caught) {
          // A failure to print is a warning on a completed sale, never an
          // error that undoes it.
          update(key, {
            status: 'FAILED',
            error:
              caught instanceof RasterError
                ? caught.message
                : caught instanceof Error && caught.message
                  ? caught.message
                  : null,
          });
        }
      }
    },
    [queuePrint, sheets, update],
  );

  const reset = useCallback(() => setStates([]), []);

  return { states, printForSale, reset };
}
